import React, { useEffect, useMemo } from "react";
import {
  DataGridPro,
  GridValueGetterParams,
  useGridApiRef,
  GridColDef,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { IFileRow } from "../../../../../webapi/service/getFIles";
import "./file.css";

export interface IProps {
  /**
   * ファイル情報一覧
   */
  rows: IFileRow[];
  /**
   * ファイルチェック行一覧
   */
  checkRows: number[];
  /**
   * チェック状態が変更された場合に呼び出されます
   * @param selects - チェック行一覧
   */
  onChangeSelectRows: (selects: number[]) => void;
  /**
   * ファイルが選択された場合に呼び出されます
   * @param rowid - 行ID
   */
  onSelecRow: (rowid: number) => void;
  /**
   * DataGridソート項目
   */
  sortModel: GridSortModel;
  /**
   * DataGridのソート項目が変更された場合に呼び出されます
   * @param sortModel - DataGridソート項目
   */
  onSetSortModel: (sortModel: GridSortModel) => void;
  /**
   * ファイル並び順が変更された場合に呼び出されます
   * @param sortIds - ファイル並び順
   */
  onSetSortIds: (sortIds: number[]) => void;
}

/**
 * ファイル一覧を表示します
 * @param props - ファイル一覧パラメータ
 * @returns ファイル一覧
 */
export default function FileLisPanel(props: IProps) {
  const {
    rows,
    checkRows,
    onChangeSelectRows,
    onSelecRow,
    sortModel,
    onSetSortModel,
    onSetSortIds,
  } = props;
  const { i18n, t } = useTranslation();
  const apiRef = useGridApiRef();

  function setCheckRows(checkRows: number[]) {
    apiRef.current.selectRows(checkRows, true, true);
  }

  useEffect(() => {
    setCheckRows(checkRows);
  }, [rows]);

  function setSortModel(sortModel: GridSortModel) {
    apiRef.current.setSortModel(sortModel);
    const sortIds = apiRef.current.getSortedRowIds();
    onSetSortIds(sortIds as number[]);
  }

  useEffect(() => {
    setSortModel(sortModel);
  }, [sortModel]);

  /**
   * ファイル一覧リスト
   */
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "fileid",
        headerName: t("project_detail_list_fileid"),
        width: 200,
        hide: true,
      },
      {
        field: "name",
        headerName: t("project_detail_list_filename"),
        width: 250,
        sortable: true,
      },
      {
        field: "inchnum",
        headerName: t("project_detail_list_input"),
        width: 150,
        sortable: true,
      },
      {
        field: "outchnum",
        headerName: t("project_detail_list_output"),
        width: 150,
        sortable: true,
      },
      {
        field: "datanum",
        headerName: t("project_detail_list_data"),
        width: 180,
        sortable: true,
      },
      {
        field: "interval",
        headerName: t("project_detail_list_interval"),
        width: 160,
        sortable: true,
      },
      {
        field: "collection",
        headerName: t("project_detail_list_acqperiod"),
        width: 360,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          return params.row.starttime + " - " + params.row.endtime;
        },
      },
      {
        field: "filetype",
        headerName: t("project_detail_list_format"),
        width: 180,
        sortable: true,
      },
      {
        field: "update",
        headerName: t("project_detail_list_lastupdate"),
        width: 160,
        sortable: true,
      },
    ];
  }, [i18n.language]);

  const dataGrid = useMemo(() => {
    return (
      <DataGridPro
        columns={columns}
        rows={rows}
        checkboxSelection
        disableSelectionOnClick
        disableColumnMenu
        onSelectionModelChange={(selecttionModel) => {
          onChangeSelectRows(selecttionModel as number[]);
        }}
        onCellClick={(params) => {
          if (params.field === "__check__") {
            return;
          }
          const row = params.row;
          onSelecRow(row.id);
        }}
        onSortModelChange={(model) => {
          onSetSortModel(model);
        }}
        getRowClassName={() => {
          return "grid-row";
        }}
        apiRef={apiRef}
        localeText={{
          noRowsLabel: "",
          footerTotalRows: t("project_detail_list_total_rows") + " : ",
          footerRowSelected: (count) => t("list_total_selecedtitem") + " : " + count,
        }}
      />
    );
  }, [rows, i18n.language]);

  return <div className="filelist-grid">{dataGrid}</div>;
}
